// ============================================================================
// RHFormsEntrevistaDesligamento_Relatorio.js
// Relatório da Pesquisa de Desligamento reformulada (padrão Clima, nota por
// seção). Lê GET /api/forms-rh/desligamento/relatorio → { linhas, notas }.
//
// A NOTA (média das médias por seção) é calculada no BACKEND
// (server/rh/desligamento-score.js, testado) e recalculada lá quando os
// filtros mudam — o front só exibe, nunca reimplementa a fórmula. Motivo do
// desligamento e "voltaria?" são contagens simples, computadas aqui.
// ============================================================================

import { useEffect, useState, useMemo, useCallback } from 'react';
import Sidebar from '../../Sidebar';
import { Filter, Download, CalendarRange } from 'lucide-react';
import { TODAS_PERGUNTAS, MOTIVOS } from './desligamento-perguntas';

// Colunas do CSV: contexto NÃO identificador (loja/data/cargo) + as perguntas.
// O relatório é ANÔNIMO — nome/CPF/matrícula nem vêm do backend (ver /relatorio).
const COLUNAS_ID = [
  { key: 'LOJA', label: 'Loja' },
  { key: 'DATA', label: 'Data' },
  { key: 'NOME_CARGO', label: 'Cargo' },
];
const COLUNAS = [...COLUNAS_ID, ...TODAS_PERGUNTAS.map((p) => ({ key: p.id, label: p.texto }))];

const MESES = [
  { v: '1', nome: 'Janeiro' }, { v: '2', nome: 'Fevereiro' }, { v: '3', nome: 'Março' },
  { v: '4', nome: 'Abril' }, { v: '5', nome: 'Maio' }, { v: '6', nome: 'Junho' },
  { v: '7', nome: 'Julho' }, { v: '8', nome: 'Agosto' }, { v: '9', nome: 'Setembro' },
  { v: '10', nome: 'Outubro' }, { v: '11', nome: 'Novembro' }, { v: '12', nome: 'Dezembro' },
];

// As 3 perguntas de texto livre (seção 8) — exibidas como feedbacks no lugar da tabela.
const CAMPOS_FEEDBACK = TODAS_PERGUNTAS.filter((p) => p.tipo === 'texto');

// 'YYYY-MM-DD' → 'DD/MM/YYYY' (as datas já chegam normalizadas em `buscar`).
const fmtBR = (iso) => String(iso).split('-').reverse().join('/');

// Cor da nota (mesma régua da Pesquisa de Clima): verde ≥4, âmbar ≥3, vermelho <3.
const corNota = (nota) => {
  if (nota === null || nota === undefined) return '#94a3b8';
  if (nota >= 4.0) return '#10b981';
  if (nota >= 3.0) return '#f59e0b';
  return '#ef4444';
};

function EntrevistaDesligamentoRelatorio({ embedded = false }) {
  // O CSV ainda carrega a coluna Cargo (uso do RH). Escondido do Gerente (2026-07-17)
  // para não reabrir pela exportação o anonimato que tiramos da tela.
  const perfil = (JSON.parse(localStorage.getItem('user') || '{}')?.perfil) || '';
  const podeExportar = perfil !== 'Gerente';

  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);

  const getMarginLeft = () => {
    if (window.innerWidth <= 480) return isCollapsed ? '60px' : '0px';
    return isCollapsed ? '100px' : '260px';
  };

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [linhas, setLinhas] = useState([]);
  const [notas, setNotas] = useState({ geral: null, secoes: [], totalRespostas: 0 });
  const [todasLojas, setTodasLojas] = useState([]); // populado só na 1ª carga
  const [anosDisponiveis, setAnosDisponiveis] = useState([]); // idem
  const [filterLoja, setFilterLoja] = useState('');
  const [filterAno, setFilterAno] = useState('');
  const [filterMes, setFilterMes] = useState('');
  const [carregando, setCarregando] = useState(false);

  // Busca no backend (com filtros na query). primeiraCarga=true guarda a lista de lojas.
  const buscar = useCallback(async ({ loja = '', inicio = '', fim = '', primeiraCarga = false } = {}) => {
    setCarregando(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token não encontrado. Faça o login novamente.');

      const qs = new URLSearchParams();
      if (loja) qs.set('loja', loja);
      if (inicio) qs.set('inicio', inicio);
      if (fim) qs.set('fim', fim);

      const url = `/api/forms-rh/desligamento/relatorio${qs.toString() ? `?${qs}` : ''}`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!res.ok) {
        let msg = `Status: ${res.status} ${res.statusText}`;
        try { const j = await res.json(); msg = j.message || JSON.stringify(j); } catch (e) { /* sem JSON */ }
        throw new Error(msg);
      }
      const data = await res.json();

      // Normaliza DATA (datetime → YYYY-MM-DD) só para exibição.
      const linhasFmt = (data.linhas || []).map((row) => {
        let d = row.DATA;
        if (row.DATA) { try { d = new Date(row.DATA).toISOString().split('T')[0]; } catch (e) { /* mantém */ } }
        return { ...row, DATA: d };
      });

      setLinhas(linhasFmt);
      setNotas(data.notas || { geral: null, secoes: [], totalRespostas: 0 });
      if (primeiraCarga) {
        setTodasLojas([...new Set(linhasFmt.map((r) => r.LOJA).filter(Boolean))].sort());
        // Anos presentes nos dados (DATA = 'YYYY-MM-DD'), do mais recente pro mais antigo.
        setAnosDisponiveis([...new Set(linhasFmt.map((r) => (r.DATA || '').slice(0, 4)).filter(Boolean))].sort().reverse());
      }
    } catch (error) {
      console.error('Erro ao buscar relatório de desligamento:', error.message);
      alert(`Erro ao buscar relatório: ${error.message}`);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { buscar({ primeiraCarga: true }); }, [buscar]);

  // Traduz o filtro Ano/Mês num intervalo de datas p/ o backend (que já aceita inicio/fim).
  // Ano sem mês = ano inteiro; ano + mês = aquele mês; sem ano = tudo. Mês sem ano é ignorado.
  const aplicarFiltros = () => {
    let inicio = ''; let fim = '';
    if (filterAno) {
      if (filterMes) {
        const mm = String(filterMes).padStart(2, '0');
        const ultimoDia = new Date(Number(filterAno), Number(filterMes), 0).getDate();
        inicio = `${filterAno}-${mm}-01`;
        fim = `${filterAno}-${mm}-${String(ultimoDia).padStart(2, '0')}`;
      } else {
        inicio = `${filterAno}-01-01`;
        fim = `${filterAno}-12-31`;
      }
    }
    buscar({ loja: filterLoja, inicio, fim });
  };

  // Período dos dados exibidos (mín/máx da DATA do conjunto atual, já filtrado).
  // Calculado aqui porque `linhas` já traz a DATA de todas as entrevistas — não
  // vale um endpoint novo só para min/max. Usa DATA (data do desligamento), a
  // mesma que os filtros de Ano/Mês recortam, e NÃO o TIMESTAMP (quando o RH
  // preencheu): assim o selo e o filtro contam a mesma história.
  const periodo = useMemo(() => {
    const datas = linhas.map((r) => r.DATA).filter(Boolean).sort(); // 'YYYY-MM-DD' ordena como texto
    return datas.length ? { de: datas[0], ate: datas[datas.length - 1] } : null;
  }, [linhas]);

  // Feedbacks abertos (seção 8): só as entrevistas que preencheram ao menos um dos 3 campos.
  const feedbacks = useMemo(
    () => linhas.filter((r) => CAMPOS_FEEDBACK.some((c) => (r[c.id] || '').trim() !== '')),
    [linhas]
  );

  // Frase do bloco de feedbacks com o DENOMINADOR explícito. Existe porque o bloco
  // lista só quem escreveu texto livre (campo opcional) e isso já foi lido como
  // "o dado parou nesta data" (2026-07-17): entrevistas sem texto somem daqui,
  // mas continuam contando na nota. Dizer "X de Y" explica a ausência.
  const resumoFeedbacks = useMemo(() => {
    const total = linhas.length;
    const com = feedbacks.length;
    if (total === 0) return 'Nenhuma entrevista no período selecionado.';
    if (com === 0) return `Nenhuma das ${total} entrevistas deixou feedback escrito — todas foram preenchidas sem texto livre.`;
    const plural = total === 1 ? 'entrevista' : 'entrevistas';
    const verbo = com === 1 ? 'deixou' : 'deixaram';
    if (com === total) return `${com} de ${total} ${plural} ${verbo} feedback escrito.`;
    return `${com} de ${total} ${plural} ${verbo} feedback escrito — ${total - com === 1 ? 'a outra foi preenchida' : `as outras ${total - com} foram preenchidas`} sem texto livre.`;
  }, [linhas, feedbacks]);

  // Distribuição do motivo (P_1) e do "voltaria?" (P_2) — contagens simples.
  const resumoMotivos = useMemo(() => {
    const cont = {};
    MOTIVOS.forEach((m) => { cont[m] = 0; });
    linhas.forEach((r) => { if (r.P_1 && cont[r.P_1] !== undefined) cont[r.P_1]++; });
    const total = linhas.length || 1;
    return MOTIVOS.map((m) => ({ motivo: m, qtd: cont[m], pct: Math.round((cont[m] / total) * 100) }))
      .sort((a, b) => b.qtd - a.qtd);
  }, [linhas]);

  const resumoVoltaria = useMemo(() => {
    let sim = 0; let nao = 0;
    linhas.forEach((r) => { if (r.P_2 === 'Sim') sim++; else if (r.P_2 === 'Não') nao++; });
    const total = sim + nao;
    return { sim, nao, pctSim: total ? Math.round((sim / total) * 100) : null };
  }, [linhas]);

  // Exportação CSV (dados brutos, uma coluna por pergunta).
  const handleExport = () => {
    if (!linhas.length) return;
    const dados = linhas.map((r) => {
      const row = {};
      for (const col of COLUNAS) row[col.label] = r[col.key] || '';
      return row;
    });
    const cabecalhos = Object.keys(dados[0]);
    const linhasCSV = dados.map((row) =>
      cabecalhos.map((campo) => {
        let v = row[campo] ? String(row[campo]) : '';
        v = v.replace(/"/g, '""');
        if (v.search(/("|,|;|\n)/g) >= 0) v = `"${v}"`;
        return v;
      }).join(';')
    );
    const csv = [cabecalhos.join(';'), ...linhasCSV].join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'desligamento_relatorio.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Cards de nota padronizados: largura igual (grid 1fr) e altura igual (grid
  // estica a linha + height 100%). Título quebra em várias linhas quando precisa.
  const CardNota = ({ titulo, nota, destaque = false }) => (
    <div style={{
      backgroundColor: '#fff',
      border: destaque ? '2px solid #e11d48' : '1px solid #e2e8f0',
      borderRadius: '14px', padding: '16px',
      display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '10px',
      height: '100%', minHeight: '104px', boxSizing: 'border-box',
    }}>
      <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', lineHeight: 1.3, wordBreak: 'break-word' }}>{titulo}</span>
      <span style={{ fontSize: '28px', fontWeight: 800, color: corNota(nota), fontVariantNumeric: 'tabular-nums' }}>
        {nota === null || nota === undefined ? '—' : nota.toFixed(2)}
      </span>
    </div>
  );

  return (
    <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

      <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease' }}>
        {!embedded && (
          <div style={{ height: '80px', backgroundColor: '#fff', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', padding: '0 40px', position: 'sticky', top: 0, zIndex: 10 }}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>Relatório de Desligamento</h2>
          </div>
        )}

        <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ marginBottom: '24px' }}>
            <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', marginBottom: '8px' }}>Relatório de Desligamento</h1>
            <p style={{ color: '#64748b', fontSize: '16px', marginBottom: '12px' }}>Notas por seção, motivos de saída e respostas das entrevistas.</p>

            {/* Selo de período: até quando o relatório tem dado. Sem ele, um bloco
                que termina numa data antiga (ex.: feedbacks) passa a impressão de
                que o dado parou ali. Reflete o filtro aplicado. */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '999px',
              padding: '7px 14px', fontSize: '13px', color: '#475569',
            }}>
              <CalendarRange size={15} color="#94a3b8" />
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {carregando
                  ? 'Carregando…'
                  : periodo
                    ? <>Dados de <strong style={{ color: '#0f172a' }}>{fmtBR(periodo.de)}</strong> a <strong style={{ color: '#0f172a' }}>{fmtBR(periodo.ate)}</strong> · {linhas.length} entrevista{linhas.length === 1 ? '' : 's'}</>
                    : 'Nenhuma entrevista no período selecionado'}
              </span>
            </div>
          </div>

          {/* FILTROS */}
          <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '16px', border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '24px', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 200px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Loja</label>
              <select value={filterLoja} onChange={(e) => setFilterLoja(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}>
                <option value="">Todas</option>
                {todasLojas.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Ano</label>
              <select value={filterAno} onChange={(e) => setFilterAno(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none' }}>
                <option value="">Todos</option>
                {anosDisponiveis.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div style={{ flex: '1 1 150px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>Mês</label>
              <select value={filterMes} onChange={(e) => setFilterMes(e.target.value)} disabled={!filterAno} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', backgroundColor: filterAno ? '#fff' : '#f1f5f9' }}>
                <option value="">Todos</option>
                {MESES.map((m) => <option key={m.v} value={m.v}>{m.nome}</option>)}
              </select>
            </div>
            <div style={{ flex: '0 1 auto', display: 'flex', gap: '10px' }}>
              <button onClick={aplicarFiltros} disabled={carregando} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#0f172a', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', opacity: carregando ? 0.6 : 1 }}>
                <Filter size={16} /> Aplicar Filtros
              </button>
              {podeExportar && (
                <button onClick={handleExport} disabled={linhas.length === 0} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#e11d48', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', opacity: linhas.length === 0 ? 0.5 : 1 }}>
                  <Download size={16} /> Extrair CSV
                </button>
              )}
            </div>
          </div>

          {/* NOTAS — todos na mesma linha (geral + seções); minmax(0,1fr) deixa
              encolherem juntos sem quebrar linha. Título quebra dentro do card. */}
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${notas.secoes.length + 1}, minmax(0, 1fr))`, gap: '12px', marginBottom: '16px' }}>
            <CardNota titulo={`Nota geral (${notas.totalRespostas} entrevista${notas.totalRespostas === 1 ? '' : 's'})`} nota={notas.geral} destaque />
            {notas.secoes.map((s) => <CardNota key={s.id} titulo={`${s.id}. ${s.titulo}`} nota={s.nota} />)}
          </div>

          {/* DETALHE POR PERGUNTA (dentro de cada seção pontuada) */}
          <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Média por pergunta</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
              {notas.secoes.map((s) => (
                <div key={s.id} style={{ border: '1px solid #f1f5f9', borderRadius: '12px', padding: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>{s.id}. {s.titulo}</span>
                    <span style={{ fontSize: '15px', fontWeight: 800, color: corNota(s.nota), fontVariantNumeric: 'tabular-nums' }}>{s.nota === null ? '—' : s.nota.toFixed(2)}</span>
                  </div>
                  {s.perguntas.map((p) => (
                    <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: '12px', color: '#64748b', padding: '3px 0' }}>
                      <span style={{ flex: 1 }}>{p.texto}</span>
                      <span style={{ fontWeight: 700, color: corNota(p.media), fontVariantNumeric: 'tabular-nums' }}>{p.media === null ? '—' : p.media.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* MOTIVO + VOLTARIA */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Motivo do desligamento</h3>
              {resumoMotivos.map((m) => (
                <div key={m.motivo} style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#475569', marginBottom: '4px' }}>
                    <span>{m.motivo}</span>
                    <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{m.qtd} ({m.pct}%)</span>
                  </div>
                  <div style={{ height: '6px', backgroundColor: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${m.pct}%`, height: '100%', backgroundColor: '#e11d48' }} />
                  </div>
                </div>
              ))}
            </div>
            <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px' }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Voltaria a trabalhar na empresa?</h3>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '34px', fontWeight: 800, color: '#10b981', fontVariantNumeric: 'tabular-nums' }}>{resumoVoltaria.pctSim === null ? '—' : `${resumoVoltaria.pctSim}%`}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>responderam "Sim"</div>
                </div>
                <div style={{ fontSize: '13px', color: '#475569' }}>
                  <div>Sim: <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{resumoVoltaria.sim}</strong></div>
                  <div>Não: <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{resumoVoltaria.nao}</strong></div>
                </div>
              </div>
            </div>
          </div>

          {/* FEEDBACKS ABERTOS (seção 8) */}
          <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px' }}>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>Feedbacks abertos</h3>
            <p style={{ margin: '0 0 18px 0', fontSize: '13px', color: '#64748b' }}>{resumoFeedbacks}</p>

            {feedbacks.length === 0 ? (
              // Sem parágrafo extra: o resumo acima já explica a ausência (o texto
              // livre é opcional — entrevista sem ele conta na nota, mas não lista aqui).
              null
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {feedbacks.map((r, index) => (
                  <div key={index} style={{ border: '1px solid #f1f5f9', borderRadius: '12px', padding: '16px' }}>
                    {/* Sem nome/CPF — relatório anônimo. Cargo e dia removidos em 2026-07-17
                        (reforça o anonimato agora que o Gerente acessa). Só loja e período aaaa-mm. */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'baseline', marginBottom: '12px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>{[r.LOJA, (r.DATA || '').slice(0, 7)].filter(Boolean).join(' · ') || '—'}</span>
                    </div>
                    {CAMPOS_FEEDBACK.map((c) => {
                      const valor = (r[c.id] || '').trim();
                      if (!valor) return null;
                      return (
                        <div key={c.id} style={{ marginBottom: '10px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '3px' }}>{c.texto}</div>
                          <div style={{ fontSize: '14px', color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{valor}</div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default EntrevistaDesligamentoRelatorio;
